// C15 — Configuración de la pantalla de planta (P1).
// Spec: docs/spec-mvp/roles/07-pantalla.md §5.
//
// El coordinador decide qué paneles rotan y en qué orden, cada cuántos
// segundos, la base del cumplimiento (capacidad|programa), y el modo andon.
// Incluye VISTA PREVIA EN VIVO embebida (iframe a /pantalla con la propia
// sesión del coordinador — su scope contiene el de la pantalla, sin
// suplantación) + botón para abrirla en pestaña completa.
//
// El directivo entra en solo lectura (igual que el resto del análisis).

import { useEffect, useMemo, useRef, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  PANELES_PANTALLA_DEFAULT,
  type BaseCumplimiento,
  type PanelPantalla,
  type TemaPantalla,
} from 'schemas';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Label } from '../../components/ui/label';
import { Select } from '../../components/ui/select';
import { Spinner } from '../../components/ui/spinner';
import { CoordinadorSidebar } from '../../components/layout/CoordinadorSidebar';
import { RoleLayout } from '../../components/layout/RoleLayout';
import { useConfigPantalla, useGuardarConfigPantalla } from '../pantalla/queries';
import { useAuth } from '../../lib/auth';
import { useVerComoActivo } from '../../lib/ver-como';

const NOMBRES: Record<PanelPantalla, { titulo: string; detalle: string }> = {
  lideres: { titulo: 'Ranking de líderes', detalle: 'Cumplimiento del turno, líder por líder' },
  podio: { titulo: 'Podio del turno', detalle: 'Top 3 líderes en vivo' },
  supervisores: { titulo: 'Ranking de supervisores', detalle: 'Producción supervisada del día' },
  lineas: { titulo: 'Líneas de producción', detalle: 'Grid de líneas con semáforo y paros' },
  turno: { titulo: 'Resumen del turno', detalle: 'Totales de planta: meta, riesgo, paros' },
  pits: { titulo: 'Entrada a Pits', detalle: 'Minutos por cambio vs meta, por causal y líder' },
};

export function C15ConfigPantallaPage() {
  const { state } = useAuth();
  const verComoActivo = useVerComoActivo();
  const [plantaId, setPlantaId] = useState<string | null>(null);
  const { data, isLoading, isError, error } = useConfigPantalla(plantaId);
  const guardar = useGuardarConfigPantalla();

  const soloLectura =
    state.kind === 'signed_in' && state.claims.rol === 'directivo' && !verComoActivo;

  // Estado del formulario: orden completo de los 5 paneles + set de activos.
  const [orden, setOrden] = useState<PanelPantalla[]>([...PANELES_PANTALLA_DEFAULT]);
  const [activos, setActivos] = useState<Set<PanelPantalla>>(new Set(PANELES_PANTALLA_DEFAULT));
  const [segundos, setSegundos] = useState(30);
  const [panelFijo, setPanelFijo] = useState<PanelPantalla | ''>('');
  const [base, setBase] = useState<BaseCumplimiento>('capacidad');
  const [andonOn, setAndonOn] = useState(true);
  const [andonMin, setAndonMin] = useState(15);
  const [tema, setTema] = useState<TemaPantalla>('oscuro');
  const [previewKey, setPreviewKey] = useState(0);
  const [panelVisto, setPanelVisto] = useState<PanelPantalla | null>(null);
  const iframeRef = useRef<HTMLIFrameElement | null>(null);

  const plantaEfectiva = plantaId ?? data?.config.planta_id ?? null;

  // Cargar el formulario cuando llega la config (o cambia la planta).
  useEffect(() => {
    if (!data) return;
    const guardados = data.config.paneles;
    setOrden([...guardados, ...PANELES_PANTALLA_DEFAULT.filter((p) => !guardados.includes(p))]);
    setActivos(new Set(guardados));
    setSegundos(data.config.segundos_rotacion);
    setPanelFijo(data.config.panel_fijo ?? '');
    setBase(data.config.base_cumplimiento);
    setAndonOn(data.config.andon_habilitado);
    setAndonMin(data.config.andon_min_paro);
    setTema(data.config.tema);
  }, [data]);

  const paneles = useMemo(() => orden.filter((p) => activos.has(p)), [orden, activos]);
  const previewUrl = plantaEfectiva ? `/pantalla?planta_id=${plantaEfectiva}` : null;

  if (state.kind !== 'signed_in') return <Navigate to="/login" replace />;

  if (isLoading) {
    return (
      <main className="flex min-h-screen flex-col items-center justify-center gap-3 bg-surface-base p-6 text-fg">
        <Spinner className="size-6" />
        <p className="text-sm text-fg-secondary">Cargando configuración de pantalla…</p>
      </main>
    );
  }
  if (isError || !data) {
    return (
      <main className="mx-auto flex min-h-screen max-w-md flex-col items-center justify-center gap-3 bg-surface-base p-6 text-center text-fg">
        <h1 className="text-lg font-medium text-crit">No pudimos cargar</h1>
        <p className="text-sm text-fg-secondary">
          {error instanceof Error ? error.message : 'Error desconocido'}
        </p>
      </main>
    );
  }

  // "Ver": muestra ESE panel en la vista previa embebida (postMessage al
  // iframe — mismo origen). Funciona incluso con el panel desactivado o con
  // el andon activo: la navegación manual gana en la pantalla.
  const verPanel = (p: PanelPantalla) => {
    setPanelVisto(p);
    iframeRef.current?.contentWindow?.postMessage(
      { tipo: 'pantalla:mostrar', panel: p },
      window.location.origin,
    );
  };

  const mover = (p: PanelPantalla, delta: -1 | 1) => {
    setOrden((prev) => {
      const i = prev.indexOf(p);
      const j = i + delta;
      if (i < 0 || j < 0 || j >= prev.length) return prev;
      const copia = [...prev];
      copia[i] = copia[j]!;
      copia[j] = p;
      return copia;
    });
  };

  const onGuardar = () => {
    if (!plantaEfectiva) return;
    if (paneles.length === 0) {
      toast.error('Active al menos un panel.');
      return;
    }
    const fijo = panelFijo && paneles.includes(panelFijo) ? panelFijo : null;
    guardar.mutate(
      {
        planta_id: plantaEfectiva,
        paneles,
        segundos_rotacion: segundos,
        panel_fijo: fijo,
        base_cumplimiento: base,
        andon_habilitado: andonOn,
        andon_min_paro: andonMin,
        tema,
      },
      {
        onSuccess: () => {
          toast.success('Configuración guardada. La pantalla la toma en menos de un minuto.');
          setPreviewKey((k) => k + 1);
        },
        onError: (e) =>
          toast.error(e instanceof Error ? e.message : 'No se pudo guardar la configuración'),
      },
    );
  };

  return (
    <RoleLayout sidebar={<CoordinadorSidebar />} roleLabel="Coordinador">
      <div className="flex flex-col gap-4">
        <header className="flex flex-wrap items-end justify-between gap-3 border-b border-border pb-3">
          <div>
            <h1 className="text-xl font-medium text-fg">Pantalla de planta</h1>
            <p className="mt-1 text-[11px] text-fg-tertiary">
              Qué muestra la TV del piso, en qué orden y contra qué base mide. La pantalla se
              actualiza sola en ≤60 s.
            </p>
          </div>
          <div className="flex items-center gap-2">
            {data.plantas.length > 1 ? (
              <Select
                value={plantaEfectiva ?? ''}
                onChange={(e) => setPlantaId(e.target.value)}
                aria-label="Planta"
              >
                {data.plantas.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.nombre}
                  </option>
                ))}
              </Select>
            ) : null}
            {previewUrl ? (
              <Button
                size="sm"
                variant="secondary"
                onClick={() => window.open(previewUrl, '_blank', 'noopener')}
              >
                Abrir en pestaña completa
              </Button>
            ) : null}
            {soloLectura ? null : (
              <Button size="sm" variant="primary" onClick={onGuardar} disabled={guardar.isPending}>
                {guardar.isPending ? 'Guardando…' : 'Guardar cambios'}
              </Button>
            )}
          </div>
        </header>

        <div className="grid gap-4 xl:grid-cols-[minmax(24rem,30rem)_1fr]">
          {/* ── Columna de configuración ── */}
          <div className="flex flex-col gap-4">
            <Card>
              <CardContent className="flex flex-col gap-1 py-4">
                <p className="mb-2 text-[11px] font-medium tracking-wide text-fg-tertiary">
                  PANELES Y ORDEN DE ROTACIÓN
                </p>
                {orden.map((p) => {
                  const on = activos.has(p);
                  return (
                    <div
                      key={p}
                      className={`flex items-center gap-3 rounded-md border px-3 py-2 ${
                        on ? 'border-border bg-surface-elevated' : 'border-border/60 opacity-60'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={on}
                        disabled={soloLectura}
                        onChange={(e) => {
                          setActivos((prev) => {
                            const next = new Set(prev);
                            if (e.target.checked) next.add(p);
                            else next.delete(p);
                            return next;
                          });
                        }}
                        className="size-4 accent-[var(--accent)]"
                        aria-label={`Mostrar ${NOMBRES[p].titulo}`}
                      />
                      <div className="min-w-0 flex-1">
                        <p className="text-sm text-fg">{NOMBRES[p].titulo}</p>
                        <p className="text-[10px] text-fg-tertiary">{NOMBRES[p].detalle}</p>
                      </div>
                      <Button
                        size="sm"
                        variant={panelVisto === p ? 'primary' : 'secondary'}
                        onClick={() => verPanel(p)}
                        aria-label={`Previsualizar ${NOMBRES[p].titulo}`}
                        title="Mostrar este panel en la vista previa"
                      >
                        Ver
                      </Button>
                      {soloLectura ? null : (
                        <div className="flex gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => mover(p, -1)}
                            aria-label="Subir"
                          >
                            ↑
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => mover(p, 1)}
                            aria-label="Bajar"
                          >
                            ↓
                          </Button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </CardContent>
            </Card>

            <Card>
              <CardContent className="grid grid-cols-2 gap-4 py-4">
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="segundos">Rotación (segundos)</Label>
                  <input
                    id="segundos"
                    type="number"
                    min={10}
                    max={300}
                    value={segundos}
                    disabled={soloLectura}
                    onChange={(e) => setSegundos(Number(e.target.value))}
                    className="rounded-md border border-border bg-surface-input px-3 py-2 text-sm text-fg"
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="panel-fijo">Panel fijo (opcional)</Label>
                  <Select
                    id="panel-fijo"
                    value={panelFijo}
                    disabled={soloLectura}
                    onChange={(e) => setPanelFijo(e.target.value as PanelPantalla | '')}
                  >
                    <option value="">— Rotación normal —</option>
                    {paneles.map((p) => (
                      <option key={p} value={p}>
                        {NOMBRES[p].titulo}
                      </option>
                    ))}
                  </Select>
                  <p className="text-[10px] text-fg-tertiary">
                    Útil para visitas: la TV se queda en un solo panel.
                  </p>
                </div>

                <div className="col-span-2 flex flex-col gap-1.5">
                  <Label>Base del cumplimiento</Label>
                  <div className="grid grid-cols-2 gap-2">
                    {(
                      [
                        [
                          'capacidad',
                          'Capacidad instalada',
                          'Real vs capacidad estándar × horas (por turno)',
                        ],
                        [
                          'programa',
                          'Programa del cliente',
                          'Real vs programa vigente (avance del día)',
                        ],
                      ] as const
                    ).map(([valor, titulo, detalle]) => (
                      <button
                        key={valor}
                        type="button"
                        disabled={soloLectura}
                        onClick={() => setBase(valor)}
                        className={`rounded-md border px-3 py-2 text-left ${
                          base === valor
                            ? 'border-border-stronger bg-surface-elevated'
                            : 'border-border opacity-70'
                        }`}
                        aria-pressed={base === valor}
                      >
                        <p className="text-sm text-fg">{titulo}</p>
                        <p className="text-[10px] text-fg-tertiary">{detalle}</p>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="col-span-2 flex flex-col gap-1.5">
                  <Label>Tema de la TV</Label>
                  <div className="grid grid-cols-2 gap-2">
                    {(
                      [
                        ['oscuro', 'Oscuro', 'Fondo negro — el look de marca'],
                        ['claro', 'Claro', 'Fondo blanco — pruebe cuál se lee mejor en planta'],
                      ] as const
                    ).map(([valor, titulo, detalle]) => (
                      <button
                        key={valor}
                        type="button"
                        disabled={soloLectura}
                        onClick={() => setTema(valor)}
                        className={`rounded-md border px-3 py-2 text-left ${
                          tema === valor
                            ? 'border-border-stronger bg-surface-elevated'
                            : 'border-border opacity-70'
                        }`}
                        aria-pressed={tema === valor}
                      >
                        <p className="text-sm text-fg">{titulo}</p>
                        <p className="text-[10px] text-fg-tertiary">{detalle}</p>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="col-span-2 flex items-center justify-between gap-4 rounded-md border border-border px-3 py-2.5">
                  <div>
                    <p className="text-sm text-fg">Modo andon</p>
                    <p className="text-[10px] text-fg-tertiary">
                      Interrumpe la rotación ante un paro largo o una línea &lt;60%.
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <label className="flex items-center gap-1.5 text-[11px] text-fg-secondary">
                      Paro ≥
                      <input
                        type="number"
                        min={5}
                        max={120}
                        value={andonMin}
                        disabled={soloLectura || !andonOn}
                        onChange={(e) => setAndonMin(Number(e.target.value))}
                        className="w-16 rounded-md border border-border bg-surface-input px-2 py-1 text-sm text-fg"
                        aria-label="Minutos de paro para el andon"
                      />
                      min
                    </label>
                    <input
                      type="checkbox"
                      checked={andonOn}
                      disabled={soloLectura}
                      onChange={(e) => setAndonOn(e.target.checked)}
                      className="size-4 accent-[var(--accent)]"
                      aria-label="Activar modo andon"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {data.config.actualizado_at ? (
              <p className="text-[10px] text-fg-tertiary">
                Última edición: {new Date(data.config.actualizado_at).toLocaleString('es-CO')}
              </p>
            ) : (
              <p className="text-[10px] text-fg-tertiary">
                Esta planta todavía usa la configuración por defecto.
              </p>
            )}
          </div>

          {/* ── Vista previa en vivo ── */}
          <div className="flex flex-col gap-2">
            <p className="text-[11px] font-medium tracking-wide text-fg-tertiary">
              VISTA PREVIA EN VIVO (con su propia sesión — lo que guarda se refleja al instante)
            </p>
            {previewUrl ? (
              <div className="overflow-hidden rounded-lg border border-border-strong bg-black shadow-elevated">
                <iframe
                  ref={iframeRef}
                  key={previewKey}
                  src={previewUrl}
                  title="Vista previa de la pantalla de planta"
                  className="aspect-video w-full"
                />
              </div>
            ) : (
              <Card>
                <CardContent className="py-8 text-center text-sm text-fg-tertiary">
                  Sin plantas asignadas a este coordinador.
                </CardContent>
              </Card>
            )}
            <p className="text-[10px] text-fg-tertiary">
              Use el botón <span className="font-medium text-fg-secondary">Ver</span> de cada panel
              para previsualizarlo acá (también los desactivados). La vista previa retoma la
              rotación normal a los dos ciclos. La TV real navega con las teclas 1–6 y ←/→; el
              usuario de la TV es de solo lectura.
            </p>
          </div>
        </div>
      </div>
    </RoleLayout>
  );
}
